import React, { LegacyRef, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import {
  DropDownListBlurEvent,
  DropDownListChangeEvent,
  DropDownListHandle,
  ListItemProps
} from "@progress/kendo-react-dropdowns";
import DropdownOption from './DropdownOption';
import SearchableDropdown from './SearchableDropdown';
import { LocationService } from '../../services';
import { LocationModel } from '../../data/entities';
import { createSyntheticEvent } from '../../utils';

interface SearchableLocationDropdownProps extends PropsWithChildren {
  value?: any;
  name: string;
  isInvalid?: boolean;
  onBlur?: (e: DropDownListBlurEvent) => void;
  onChange?: (e: DropDownListChangeEvent) => void;
  onSelect?: (value: DropdownOption) => void;
  placeholder?: string;
  locations?: DropdownOption[];
  enableRef?: boolean; 
  filterLocation?: number[];
  defaultItem?: boolean;
}

export interface LocationDropdownOption extends DropdownOption {
  code?: string;
}

export const SearchableLocationDropdown = React.forwardRef(
  (
    props: Readonly<SearchableLocationDropdownProps>,
    ref: LegacyRef<DropDownListHandle> | undefined
  ) => {
    const dropdownRef = useRef(null)
    const locationService = useMemo(() => new LocationService(), []);
    const [options, setOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {

      if(props.locations){
        setOptions(props.locations);

        if(props.enableRef){
          let activeRef = (ref ?? dropdownRef as any)?.current as DropDownListHandle;
          let select = activeRef?.element?.querySelector('select');
       
          if (select) {
            const event = new Event('select', { bubbles: true });
            Object.defineProperty(event, 'target', { writable: false, value: select });
            const syntheticEvent = createSyntheticEvent(event) as React.ChangeEvent<typeof select>;
            props.onChange?.call(this, {
              nativeEvent: event,
              syntheticEvent: syntheticEvent,
              target: activeRef,
              value: activeRef.value
            });
          }
        }
      }
      else{
        const fetchData = async () => {
          const locations =  await locationService.getAll();
          
          if(props.filterLocation){
            const filteredLocations = locations.filter((location) => props.filterLocation?.includes(location.id as number));
            const newOptions =  filteredLocations.map<LocationDropdownOption>((location: LocationModel) => ({
              value: location.id?.toString() ?? "",
              text: `${location.name}`,
              code: location.businessCode,
            }));
            const sortedLocation = newOptions.sort((a, b) => a.text.localeCompare(b.text));
            setOptions(sortedLocation);
          }else{
            const newOptions = locations.map<LocationDropdownOption>((location: LocationModel) => ({
              value: location.id?.toString() ?? "",
              text: `${location.name}`,
              code: location.businessCode,
            }));
            
            const sortedLocation = newOptions.sort((a, b) => a.text.localeCompare(b.text));
            setOptions(sortedLocation);
          }
        };
        fetchData();
      }
      
  
    }, [props.locations, locationService]);

    const getValue = () => {
      if (props.value || props.value === "" ) {
        return options.find((option) => option.value === props.value?.toString());
      };
    };

    const itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
      const item = itemProps.dataItem as LocationDropdownOption;

      return React.cloneElement(li, li.props,
        <span>
          {item.text}
          {item.code && <span className="text-muted"> ({item.code})</span>}
        </span>
      );
    };

    return (
      <SearchableDropdown
        defaultItem={props.defaultItem ? { text: "Select a location...", value: "-1" } : undefined}
        ref={ref ?? dropdownRef}
        label="Location"
        name={props.name}
        itemRender={itemRender}
        filterFields={["text", "code"]}
        options={options}
        value={getValue()}
        onChange={props.onChange}
        onSelect={props.onSelect}
        isInvalid={props.isInvalid}
      >
        {props.children}
      </SearchableDropdown>
    );
  }
);

export default SearchableLocationDropdown